import { isLocalePhoneSupported } from "@faire/help-center-self-serve/utils/isLocalePhoneSupported";
import { clearLocalStorage } from "@faire/help-center-self-serve/utils/localStorage";
import { BackToCta } from "@faire/help-center-utils/components/BackToCta/BackToCta";
import { BackToDestination } from "@faire/help-center-utils/components/BackToCta/types";
import { SupportType } from "@faire/help-center-utils/lib/dateInTimeZone";
import { browserLevelLocaleKey } from "@faire/help-center-utils/serialized-data/getLocale";
import { getUserPhoneNumber } from "@faire/help-center-utils/serialized-data/getUser";
import { getSettingPhoneSLA } from "@faire/help-center-utils/settings/getSettingPhoneSLA";
import { Email } from "@faire/web--source/slate/icons/foundation/Email";
import { Phone } from "@faire/web--source/slate/icons/foundation/Phone";
import { Flex } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import React from "react";
import styled from "styled-components";

import { useScrollToTopOnLoadEffect } from "../../hooks/useScrollToTopEffect";
import { useFormatTime } from "../Answer/hooks/useFormatTime";

import { IconWrapper } from "./styles";

interface IEmailSubmittedProps {
  supportType: SupportType;
}

export const EmailSubmitted: React.FC<IEmailSubmittedProps> = ({
  supportType,
}) => {
  useScrollToTopOnLoadEffect();
  const phoneNumber = getUserPhoneNumber();
  const waitTime = getSettingPhoneSLA();
  const formattedWaitTime = useFormatTime(waitTime);

  const getPhoneMessage = () => {
    const showLanguageNotice =
      !isLocalePhoneSupported(browserLevelLocaleKey());

    if (showLanguageNotice) {
      return (
        <Typography>
          <StrictLocalMsg
            defaultMessage="A customer support agent will review details and call you at {phoneNumber}. Average wait time is {formattedWaitTime}. Please note that calls are currently only available in English."
            description={{
              text: "Tells the user that Faire's support team will review and respond to their request with english disclaimer",
            }}
            values={{
              phoneNumber,
              formattedWaitTime,
            }}
          />
        </Typography>
      );
    }
    return (
      <Typography>
        <StrictLocalMsg
          defaultMessage="A customer support agent will review details and call you at {phoneNumber}. Average wait time is {formattedWaitTime}."
          description={{
            text: "Tells the user that Faire's support team will review and respond to their request",
          }}
          values={{
            phoneNumber,
            formattedWaitTime,
          }}
        />
      </Typography>
    );
  };

  return (
    <>
      <IconWrapper>
        {supportType === "phone" ? (
          <Phone fontSize="24px" />
        ) : (
          <Email fontSize="24px" />
        )}
      </IconWrapper>
      <Spacer height="4x" />
      {supportType === "phone" ? (
        <Typography variant="sectionHeaderSansMedium">
          <StrictLocalMsg
            defaultMessage="We’ve received your call request"
            description={{
              text: "Title for the page that let user know that their submission is successful",
            }}
          />
        </Typography>
      ) : (
        <Typography variant="sectionHeaderSansMedium">
          <StrictLocalMsg
            defaultMessage="We’ve received your request"
            description={{
              text: "Title for the page that let user know that their submission is successful",
              img: "https://cdn.faire.com/fastly/41a5c3d5db35c5cb7dd2c22e7883cdaa49ef098ce3498c95dafab0f298ae645c.png",
            }}
          />
        </Typography>
      )}
      <Spacer height="1x" />
      {supportType === "phone" ? (
        getPhoneMessage()
      ) : (
        <Typography>
          <StrictLocalMsg
            defaultMessage="A customer support agent will review details and reply by email within 1 business day."
            description={{
              text: "Tells the user that Faire's support team will review and respond to their request",
              img: "https://cdn.faire.com/fastly/41a5c3d5db35c5cb7dd2c22e7883cdaa49ef098ce3498c95dafab0f298ae645c.png",
            }}
          />
        </Typography>
      )}
      <Spacer height="3x" />
      <ButtonWrapper>
        <BackToCta
          destination={BackToDestination.HELP_CENTER}
          onClick={clearLocalStorage}
        />
        <Spacer width="2x" />
        <BackToCta
          variant="secondary"
          destination={BackToDestination.FAIRE}
          onClick={clearLocalStorage}
        />
      </ButtonWrapper>
    </>
  );
};

const ButtonWrapper = styled(Flex).attrs({
  direction: { tabletAndAbove: "row", mobileAndAbove: "column" },
})``;
